// Field schemas for each admin-managed content type.

const commonPublished = { key: "published", type: "boolean", label: "Published (visible on site)", help: "Uncheck to hide from public site while keeping the record." };

export const CONTENT_SCHEMAS = {
    services: {
        label: "Services",
        singular: "Service",
        fields: [
            { key: "title", type: "text", label: "Title *", placeholder: "e.g., Product Engineering" },
            { key: "slug", type: "text", label: "Slug (URL segment)", help: "Auto-generated if left empty. Lowercase, dashes only." },
            { key: "tagline", type: "text", label: "Tagline", placeholder: "Short punchy line" },
            { key: "overview", type: "textarea", label: "Overview", rows: 4 },
            { key: "image", type: "image", label: "Cover image" },
            { key: "challenges", type: "array", label: "Business challenges", itemPlaceholder: "One challenge per row" },
            { key: "approach", type: "textarea", label: "RKS approach", rows: 3 },
            { key: "capabilities", type: "array", label: "Capabilities", itemPlaceholder: "One capability per row" },
            { key: "stack", type: "array", label: "Technology stack (tags)", itemPlaceholder: "e.g., React" },
            { key: "governance", type: "textarea", label: "Delivery governance", rows: 3 },
            { key: "engagements", type: "array", label: "Engagement models", itemPlaceholder: "e.g., Milestone-Based" },
            { key: "faqs", type: "faqs", label: "FAQs" },
            { key: "order", type: "number", label: "Display order (lower first)", placeholder: "1" },
            commonPublished,
        ],
        columns: [
            { key: "order", label: "#", width: "w-14" },
            { key: "title", label: "Title" },
            { key: "slug", label: "Slug" },
            { key: "published", label: "Published", render: (r) => r.published ? "Yes" : "No" },
        ],
        idField: "slug",
    },
    industries: {
        label: "Industries",
        singular: "Industry",
        fields: [
            { key: "name", type: "text", label: "Name *", placeholder: "e.g., Retail" },
            { key: "slug", type: "text", label: "Slug", help: "Auto-generated from name if empty." },
            { key: "headline", type: "text", label: "Headline" },
            { key: "value_prop", type: "textarea", label: "Value proposition", rows: 3 },
            { key: "image", type: "image", label: "Cover image" },
            { key: "challenges", type: "array", label: "Challenges", itemPlaceholder: "Challenge" },
            { key: "offerings", type: "array", label: "Offerings", itemPlaceholder: "Offering" },
            { key: "recommended_services", type: "array", label: "Recommended services", itemPlaceholder: "e.g., Product Engineering" },
            { key: "outcomes", type: "array", label: "Business outcomes", itemPlaceholder: "Outcome" },
            { key: "order", type: "number", label: "Display order" },
            commonPublished,
        ],
        columns: [
            { key: "order", label: "#", width: "w-14" },
            { key: "name", label: "Name" },
            { key: "slug", label: "Slug" },
            { key: "published", label: "Published", render: (r) => r.published ? "Yes" : "No" },
        ],
        idField: "slug",
    },
    products: {
        label: "Products",
        singular: "Product",
        fields: [
            // ── Hero ──
            { key: "title", type: "text", label: "Product Name *", placeholder: "e.g., RKS Intelligent InvoiceOS" },
            { key: "eyebrow", type: "text", label: "Eyebrow / Badge", placeholder: "e.g., AGENTIC AI FOR ACCOUNTS PAYABLE", help: "Short uppercase badge shown above the headline." },
            { key: "hero_headline", type: "text", label: "Hero Headline", placeholder: "e.g., Automate the invoice lifecycle.", help: "Large H1 in the hero. Falls back to product name if empty." },
            { key: "hero_description", type: "textarea", label: "Hero Description", rows: 3, placeholder: "Shown in the hero below the headline." },
            { key: "category", type: "text", label: "Category *", placeholder: "e.g., Finance & Accounts Payable, BFSI, Retail" },
            { key: "description", type: "textarea", label: "Short Description (listing card)", rows: 3 },
            { key: "thumbnail", type: "image", label: "Thumbnail image" },
            { key: "video_url", type: "video", label: "Product Demo Video", help: "Upload an MP4/WebM video, or paste a YouTube embed URL (e.g. https://www.youtube.com/embed/...)." },
            { key: "tech_stack", type: "array", label: "Tech Stack", itemPlaceholder: "e.g., Kafka" },
            // ── Outcome Strip ──
            { key: "outcome_strip", type: "object-array", label: "Outcome Strip (4 KPI panels below hero)", help: "Each panel has a label, title, and description. Shows as a 4-column strip.",
              subfields: [
                { key: "label", label: "Label", placeholder: "e.g., Automation" },
                { key: "title", label: "Title", placeholder: "e.g., Reduce repetitive AP work" },
                { key: "description", label: "Description", placeholder: "1-2 sentence description", type: "textarea" },
              ]},
            // ── How It Works ──
            { key: "workflow_steps", type: "object-array", label: "Workflow Steps (\"How It Works\" section)", help: "Numbered steps shown in a horizontal timeline.",
              subfields: [
                { key: "number", label: "Number", placeholder: "e.g., 01" },
                { key: "title", label: "Step Title", placeholder: "e.g., Collect" },
                { key: "description", label: "Description", placeholder: "Step description", type: "textarea" },
              ]},
            // ── Agents ──
            { key: "agents", type: "object-array", label: "AI Agents (\"Agent Architecture\" section)", help: "Each agent card with name, responsibility, and tags.",
              subfields: [
                { key: "name", label: "Agent Name", placeholder: "e.g., Invoice Collection Agent" },
                { key: "responsibility", label: "Responsibility", placeholder: "What this agent does", type: "textarea" },
                { key: "tags", label: "Tags (comma-separated)", placeholder: "e.g., ERP, Email, SharePoint", type: "comma-array" },
              ]},
            // ── Business Problems ──
            { key: "business_problems", type: "object-array", label: "Business Problems (structured cards)", help: "Shown as a 2-column grid of problem cards.",
              subfields: [
                { key: "title", label: "Problem Title", placeholder: "e.g., Manual Work" },
                { key: "description", label: "Description", placeholder: "Problem description", type: "textarea" },
              ]},
            { key: "business_problem", type: "textarea", label: "Business Problem (plain text fallback)", rows: 4, help: "Used only if no structured Business Problems above are set." },
            // ── Features ──
            { key: "features", type: "array", label: "Key Features", itemPlaceholder: "e.g., Real-time event streaming with sub-second latency" },
            // ── Architecture Diagram ──
            { key: "architecture_diagram", type: "image", label: "Architecture Diagram", help: "Upload a system/architecture diagram (PNG, WebP)." },
            // ── Outcomes ──
            { key: "outcomes", type: "object-array", label: "Business Outcomes (\"What you gain\" section)", help: "4 outcome cards shown in a grid.",
              subfields: [
                { key: "label", label: "Label", placeholder: "e.g., Less Manual Work" },
                { key: "description", label: "Description", placeholder: "Outcome description", type: "textarea" },
              ]},
            commonPublished,
        ],
        columns: [
            { key: "title", label: "Title" },
            { key: "category", label: "Category" },
            { key: "published", label: "Published", render: (r) => r.published ? "Yes" : "No" },
        ],
        idField: "id",
    },
    blogs: {
        label: "Blog posts",
        singular: "Blog post",
        fields: [
            { key: "title", type: "text", label: "Title *" },
            { key: "author", type: "text", label: "Author" },
            { key: "category", type: "text", label: "Category" },
            { key: "excerpt", type: "textarea", label: "Excerpt (1-2 lines)", rows: 2 },
            { key: "cover_image", type: "image", label: "Cover image" },
            { key: "content", type: "textarea", label: "Content (Markdown / plain text)", rows: 12 },
            { key: "published_at", type: "text", label: "Published at (ISO 8601)", placeholder: "2026-01-14T09:00:00Z" },
            commonPublished,
        ],
        columns: [
            { key: "title", label: "Title" },
            { key: "category", label: "Category" },
            { key: "author", label: "Author" },
            { key: "published_at", label: "Date", render: (r) => r.published_at ? new Date(r.published_at).toLocaleDateString() : "—" },
        ],
        idField: "id",
    },
    "success-stories": {
        label: "Success stories",
        singular: "Success story",
        fields: [
            // ── MODULE 1: Case Study Header & Hero Impact Bar ──
            { key: "_m1", type: "section", label: "Module 1 — Case Study Header & Hero Impact Bar" },
            { key: "title",        type: "text",     label: "Page Title / H1 *",               placeholder: "e.g., Real-Time Retail Inventory Intelligence & AI Demand Forecasting Platform" },
            { key: "sub_headline", type: "textarea", label: "Sub-headline (Value Proposition)", rows: 3, placeholder: "Concise value prop summary shown below the title in the hero." },
            { key: "slug",         type: "text",     label: "URL Slug / Path",                  placeholder: "e.g., real-time-retail-inventory-ai", help: "Auto-generated from title if empty. Lowercase, dashes only. URL: /success-stories/{slug}" },
            { key: "industry",     type: "text",     label: "Industry",                         placeholder: "e.g., Retail, BFSI, Logistics, Manufacturing" },
            { key: "cover_image",  type: "image",    label: "Cover Image" },
            { key: "impact_metrics", type: "object-array", label: "Executive Impact Bar — Metric Cards (up to 4)",
              help: "Key outcome KPIs displayed in the hero impact strip below the title.",
              subfields: [
                { key: "value",    label: "Metric Value",   placeholder: "e.g., +20%" },
                { key: "label",    label: "Card Label",     placeholder: "e.g., Retail Sales Revenue Growth" },
                { key: "sub_text", label: "Sub-text / Tooltip", placeholder: "Contextual explanation for this verified outcome", type: "textarea" },
              ]},

            // ── MODULE 2: Problem-Solution & Architecture Diagram ──
            { key: "_m2", type: "section", label: "Module 2 — Problem-Solution & Architecture Diagram Manager" },
            { key: "challenge", type: "textarea", label: "Challenge Overview", rows: 4 },
            { key: "pain_points", type: "object-array", label: "Challenge & Pain Points Matrix (Challenge → Root Cause → Business Impact)",
              help: "Tabular row editor. Add, reorder, or hide specific operational challenge rows.",
              subfields: [
                { key: "challenge",       label: "Challenge",       placeholder: "e.g., Inventory data latency across 24 stores" },
                { key: "root_cause",      label: "Root Cause",      placeholder: "e.g., Batch-based nightly sync jobs" },
                { key: "business_impact", label: "Business Impact", placeholder: "e.g., 18-24 hour data stale window causing stockouts" },
              ]},
            { key: "solution", type: "textarea", label: "Solution Overview", rows: 4 },
            { key: "architecture_diagram", type: "image", label: "Architecture Diagram Asset (SVG / 1:1 PNG)",
              help: "Upload a high-resolution SVG or square PNG architecture diagram." },
            { key: "architecture_layers", type: "object-array", label: "Architecture Layer Breakdown (4-step flow)",
              help: "Repeater for the architectural flow steps (e.g., 1. Store Layer, 2. Event Ingestion Layer...).",
              subfields: [
                { key: "step",        label: "Step #",      placeholder: "1" },
                { key: "name",        label: "Layer Name",  placeholder: "e.g., Store Layer" },
                { key: "description", label: "Description", placeholder: "What happens at this layer", type: "textarea" },
              ]},
            { key: "technical_capabilities", type: "textarea", label: "Deep-Dive Technical Capabilities (bulleted architectural features)", rows: 5,
              placeholder: "Event Streaming — sub-second latency with Apache Kafka\nCaching — Redis hot-path cache for real-time reads\nAI Demand Engine — LSTM-based demand forecasting..." },

            // ── MODULE 3: Verified Outcomes & Technology Stack ──
            { key: "_m3", type: "section", label: "Module 3 — Verified Outcomes & Technology Stack" },
            { key: "results", type: "array", label: "Results / Verified Outcomes", itemPlaceholder: "e.g., +20% Retail Sales Revenue Growth" },
            { key: "roi_matrix", type: "object-array", label: "Implementation Quality & ROI Matrix",
              help: "Data grid for performance categories with quality standards and verified business outcomes.",
              subfields: [
                { key: "category",         label: "Performance Category",            placeholder: "e.g., Engineering Velocity" },
                { key: "quality_standard", label: "Quality Implementation Standard", placeholder: "e.g., CI/CD with automated test gates", type: "textarea" },
                { key: "verified_outcome", label: "Verified Business Outcome",       placeholder: "e.g., 60% faster feature delivery", type: "textarea" },
              ]},
            { key: "tech_stack", type: "array", label: "Technology Stack (flat tags)", itemPlaceholder: "e.g., Apache Kafka",
              help: "Flat list for backward compatibility. Use Categorized Stack below for the full taxonomy." },
            { key: "tech_stack_categories", type: "object-array", label: "Technology Stack — Categorized Taxonomy",
              help: "Categories: Streaming & Ingestion | Compute & Orchestration | Database & Storage | Frontend & Analytics",
              subfields: [
                { key: "category",     label: "Category",                      placeholder: "e.g., Streaming & Ingestion" },
                { key: "technologies", label: "Technologies (comma-separated)", placeholder: "e.g., Apache Kafka, AWS EventBridge, REST APIs, Spring Boot", type: "comma-array" },
              ]},

            // ── MODULE 4: High-Conversion Lead Capture & PDF Asset Engine ──
            { key: "_m4", type: "section", label: "Module 4 — High-Conversion Lead Capture & PDF Asset Engine" },
            { key: "cta_headline",     type: "text",     label: "Closing CTA Headline",   placeholder: "e.g., Ready to modernize your supply chain?" },
            { key: "cta_sub_copy",     type: "textarea", label: "Closing CTA Sub-copy",   rows: 2, placeholder: "Let's explore how RKS Systems can deliver similar outcomes for your business." },
            { key: "cta_primary_url",  type: "text",     label: "Primary CTA Button URL (Schedule Meeting)",
              placeholder: "/schedule-meeting?intent=architecture&source=retail_case_study",
              help: "Maps to /schedule-meeting with pre-filled query params for intent & source tracking." },
            { key: "pdf_asset_url",    type: "text",     label: "PDF Case Study Asset URL",
              placeholder: "https://... or /api/media/...",
              help: "Direct URL to the downloadable Architecture & ROI Case Study PDF." },
            { key: "pdf_gate_email",   type: "boolean",  label: "Gate PDF behind work email capture",
              help: "When checked, visitors must submit their work email before the PDF downloads." },
            { key: "cross_sell_product", type: "text",   label: "Product Cross-Sell Callout (name)",
              placeholder: "e.g., InvoiceOS AP Automation",
              help: "Enter a product name to show a contextual cross-sell callout at the bottom of this case study. Leave blank to hide." },

            commonPublished,
        ],
        columns: [
            { key: "title", label: "Title" },
            { key: "industry", label: "Industry" },
            { key: "published", label: "Published", render: (r) => r.published ? "Yes" : "No" },
        ],
        idField: "id",
    },
    "cost-roles": {
        label: "Cost Roles",
        singular: "Cost Role",
        fields: [
            { key: "name", type: "text", label: "Role name *", placeholder: "e.g., Cloud Engineer" },
            { key: "rks_hourly_usd", type: "number", label: "RKS hourly rate (USD) *", placeholder: "30" },
            { key: "us_hourly_usd", type: "number", label: "US onshore benchmark rate (USD) *", placeholder: "100" },
            { key: "order", type: "number", label: "Display order (lower first)", placeholder: "1" },
            commonPublished,
        ],
        columns: [
            { key: "order", label: "#", width: "w-14" },
            { key: "name", label: "Role" },
            { key: "rks_hourly_usd", label: "RKS $/hr", render: (r) => `$${r.rks_hourly_usd}` },
            { key: "us_hourly_usd", label: "US $/hr", render: (r) => `$${r.us_hourly_usd}` },
            { key: "published", label: "Active", render: (r) => r.published === false ? "No" : "Yes" },
        ],
        idField: "id",
    },
    "cost-challenges": {
        label: "Engineering Challenges",
        singular: "Engineering Challenge",
        fields: [
            { key: "name", type: "text", label: "Challenge name *", placeholder: "e.g., Cloud Migration" },
            { key: "description", type: "textarea", label: "Description", rows: 2, help: "Shown under the dropdown on the Cost Explorer." },
            { key: "recommended_roles", type: "array", label: "Recommended roles", itemPlaceholder: "Must match a Cost Role name exactly, e.g., Cloud Engineer", help: "Selecting this challenge pre-fills the team builder with these roles." },
            { key: "order", type: "number", label: "Display order (lower first)", placeholder: "1" },
            commonPublished,
        ],
        columns: [
            { key: "order", label: "#", width: "w-14" },
            { key: "name", label: "Challenge" },
            { key: "recommended_roles", label: "Recommended roles", render: (r) => (r.recommended_roles || []).join(", ") || "—" },
            { key: "published", label: "Active", render: (r) => r.published === false ? "No" : "Yes" },
        ],
        idField: "id",
    },
    "engagement-models": {
        label: "Engagement Models",
        singular: "Engagement Model",
        fields: [
            { key: "name", type: "text", label: "Name *", placeholder: "e.g., Milestone-Based" },
            { key: "slug", type: "text", label: "Slug (URL fragment)", help: "Auto-generated from name if empty. Used as #anchor on /engagement-models." },
            { key: "tagline", type: "text", label: "Tagline", placeholder: "Short one-liner" },
            { key: "overview", type: "textarea", label: "Overview", rows: 4 },
            { key: "benefits", type: "array", label: "Benefits", itemPlaceholder: "e.g., Predictable budget" },
            { key: "governance", type: "array", label: "Governance", itemPlaceholder: "e.g., Bi-weekly steering" },
            { key: "lifecycle", type: "array", label: "Delivery lifecycle (optional)", itemPlaceholder: "e.g., Discovery & scoping" },
            { key: "reporting", type: "array", label: "Reporting (optional)", itemPlaceholder: "e.g., Weekly hours & burndown" },
            { key: "cost_visibility", type: "textarea", label: "Cost visibility", rows: 2 },
            { key: "order", type: "number", label: "Display order (lower first)", placeholder: "1" },
            commonPublished,
        ],
        columns: [
            { key: "order", label: "#", width: "w-14" },
            { key: "name", label: "Name" },
            { key: "slug", label: "Slug" },
            { key: "published", label: "Published", render: (r) => r.published === false ? "No" : "Yes" },
        ],
        idField: "slug",
    },
};

export function getSchema(kind) {
    return CONTENT_SCHEMAS[kind];
}
